'use client';

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from "@/hooks/useTranslation";
import { Card, CardContent } from '@/components/ui/card';
import { highlightKeywords } from "@/lib/utils";
import { Building2, Users, Globe, Heart, Award, Headphones } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function WhyChooseSection() {
    const { t, isRTL } = useTranslation("all-on-4");
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 80%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            if (cardsRef.current?.children) {
                gsap.fromTo(cardsRef.current.children,
                    { opacity: 0, y: 40 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        stagger: 0.1,
                        scrollTrigger: {
                            trigger: cardsRef.current,
                            start: "top 75%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const benefits = [
        {
            icon: Building2,
            title: t("whyChoose.benefits.modernFacilities.title"),
            description: t("whyChoose.benefits.modernFacilities.description")
        },
        {
            icon: Users,
            title: t("whyChoose.benefits.specializedProfessionals.title"),
            description: t("whyChoose.benefits.specializedProfessionals.description")
        },
        {
            icon: Globe,
            title: t("whyChoose.benefits.globalPatientCare.title"),
            description: t("whyChoose.benefits.globalPatientCare.description")
        },
        {
            icon: Heart,
            title: t("whyChoose.benefits.conciergeSupport.title"),
            description: t("whyChoose.benefits.conciergeSupport.description")
        },
        {
            icon: Award,
            title: t("whyChoose.benefits.premiumMaterials.title"),
            description: t("whyChoose.benefits.premiumMaterials.description")
        },
        {
            icon: Headphones,
            title: t("whyChoose.benefits.multilingualTeam.title"),
            description: t("whyChoose.benefits.multilingualTeam.description")
        }
    ];

    return (
        <section
            id="why-choose"
            ref={sectionRef}
            className="py-16 lg:py-24 bg-slate-50 dark:bg-navy-950 min-h-[800px]"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2
                        ref={titleRef}
                        className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6"
                    >
                        {highlightKeywords(t("whyChoose.title"), ["DentaWista", "All-On-4"])}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("whyChoose.subtitle")}
                    </p>
                </div>

                <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {benefits.map((benefit, index) => (
                        <div key={index}>
                            <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
                                <CardContent className="p-8 text-center">
                                    <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-gold/30 transition-colors">
                                        <benefit.icon className="w-8 h-8 text-gold" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-navy dark:text-white mb-4">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                        {benefit.description}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
} 